import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import {
  LayoutDashboard,
  Building2,
  Calendar,
  FileText,
  TrendingUp,
  TrendingDown,
  Megaphone,
  BarChart3,
  Download,
  Settings,
  CreditCard,
  Users,
  CheckSquare,
  Menu,
  Globe,
} from 'lucide-react';

type NavItem = {
  key: string;
  icon: any;
  path: string;
  labelOverride?: string;
};

const navItems: NavItem[] = [
  { key: 'portfolio', icon: LayoutDashboard, path: '/portfolio' },
  { key: 'properties', icon: Building2, path: '/properties' },
  { key: 'calendar', icon: Calendar, path: '/calendar' },
  { key: 'leases', icon: FileText, path: '/leases' },
  { key: 'tenants', icon: Users, path: '/tenants' },
  { key: 'tasks', icon: CheckSquare, path: '/tasks' },
  { key: 'income', icon: TrendingUp, path: '/income' },
  { key: 'expenses', icon: TrendingDown, path: '/expenses' },
  { key: 'ads', icon: Megaphone, path: '/ads' },
  { key: 'analytics', icon: BarChart3, path: '/analytics' },
  { key: 'exports', icon: Download, path: '/exports' },
  { key: 'seoManager', icon: Globe, path: '/admin/seo', labelOverride: 'SEO Manager' },
  { key: 'tariffs', icon: CreditCard, path: '/tariffs' },
  { key: 'settings', icon: Settings, path: '/settings' },
];

export const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      setIsAdmin(!!data);
    };

    checkAdmin();
  }, []);

  const NavContent = () => (
    <nav className="space-y-1 p-4">
      {navItems.map((item) => {
        // Hide SEO Manager for non-admins
        if (item.key === 'seoManager' && !isAdmin) {
          return null;
        }

        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        
        return (
          <Link
            key={item.key}
            to={item.path}
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            )}
          >
            <Icon className="h-5 w-5" />
            {item.labelOverride || t(`nav.${item.key}`)}
          </Link>
        );
      })}
    </nav>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="fixed left-4 top-4 z-50 md:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 p-0">
          <div className="flex h-16 items-center justify-center border-b border-border px-6">
            <h1 className="text-xl font-bold">Rental Manager</h1>
          </div>
          <NavContent />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className="w-64 border-r border-border bg-card">
      <div className="flex h-16 items-center justify-center border-b border-border px-6">
        <h1 className="text-xl font-bold">Rental Manager</h1>
      </div>
      <NavContent />
    </aside>
  );
};
